import {
  BadgeTemplate,
  BadgeElement,
  BadgeElementType,
  BadgeImageData,
  BadgeTextData,
  BadgeFieldData,
  BadgeLineData,
  BadgeShapeData,
} from "sharedTypes/badge"
import { MediaColorMode } from "components/BadgeBuilder/utils/printers"
import { getCapability, getTemplateDimensions } from "components/BadgeBuilder/utils/badgeHelpers"
import { getElementAABB } from "components/CanvasEditor/lib/geometry"
import { getElementDisplayName } from "components/BadgeBuilder/Panels/LayersPanel/helpers"

export type ValidationError = {
  type: "empty_image" | "empty_text" | "off_badge" | "incompatible_color"
  /** What's wrong, in a few words: "No image uploaded", "Text color #ff00aa". */
  message: string
  elementId?: string
  elementType?: BadgeElementType
  /** The element's name, as the Layers panel shows it. */
  elementLabel?: string
  fieldName?: string
  currentColor?: string
}

const isColorAllowed = (color: string, colorMode: MediaColorMode): boolean => {
  if (!color) return true // Empty colors are allowed
  if (color.toLowerCase() === "transparent") return true // Transparent is always allowed (no ink)

  const normalizedColor = color.toLowerCase().startsWith("#") ? color.toLowerCase() : `#${color.toLowerCase()}`

  if (colorMode === "full-color") {
    return true
  }

  const allowedColors = getSuggestedColors(colorMode)

  return allowedColors.some(
    (allowedColor) => allowedColor.toLowerCase() === normalizedColor,
  )
}

const getSuggestedColors = (colorMode: MediaColorMode): string[] => {
  if (colorMode === "black-only") {
    return ["#000000", "#ffffff"]
  }
  if (colorMode === "black-red") {
    return ["#000000", "#ffffff", "#ff0000"]
  }

  return []
}

const getColorModeName = (colorMode: MediaColorMode): string => {
  switch (colorMode) {
    case "black-only":
      return "black and white only"
    case "black-red":
      return "black, white, and red only"
    case "full-color":
      return "full color"
    default:
      return "unknown"
  }
}

// The colors each element type has, by form field
type ElementColor = { fieldName: string; label: string; color?: string }

const elementColors = (element: BadgeElement): ElementColor[] => {
  const own = (() => {
    switch (element.type) {
      case BadgeElementType.TEXT:
      case BadgeElementType.FIELD:
        return [{
          fieldName: "color", label: "Text color", color: (element.data as BadgeTextData | BadgeFieldData).color,
        }]
      case BadgeElementType.LINE:
        return [{ fieldName: "color", label: "Line color", color: (element.data as BadgeLineData).color }]
      case BadgeElementType.RECTANGLE:
      case BadgeElementType.OVAL: {
        const shape = element.data as BadgeShapeData

        return [
          { fieldName: "strokeColor", label: "Outline color", color: shape.strokeColor },
          { fieldName: "fillColor", label: "Fill color", color: shape.fillColor },
        ]
      }
      default:
        return []
    }
  })()

  return [
    ...own,
    { fieldName: "border.color", label: "Border color", color: element.border?.color },
    { fieldName: "fill", label: "Background color", color: element.fill },
  ]
}

// The Layers panel's names, numbered when several elements share one ("Image 1", "Image 2")
const numberedLabels = (elements: BadgeElement[]) => {
  const names = elements.map(getElementDisplayName)
  const seen = new Map<string, number>()

  return names.map((name) => {
    if (names.filter((other) => other === name).length === 1) return name

    const count = (seen.get(name) ?? 0) + 1
    seen.set(name, count)

    return `${name} ${count}`
  })
}

export const validateBadgeTemplate = (template: BadgeTemplate): ValidationError[] => {
  const errors: ValidationError[] = []
  const { colorMode } = getCapability(template.printerModel, template.printerMedia)
  const printableColors = getColorModeName(colorMode)
  const labels = numberedLabels(template.badgeData.elements)
  const badge = getTemplateDimensions(template)

  template.badgeData.elements.forEach((element: BadgeElement, index) => {
    const about = {
      elementId: element.id,
      elementType: element.type,
      elementLabel: labels[index],
    }

    if (element.type === BadgeElementType.IMAGE) {
      const imageData = element.data as BadgeImageData
      if (!imageData.url || imageData.url.trim() === "") {
        errors.push({ type: "empty_image", message: "No image uploaded", ...about })
      }
    }

    if (element.type === BadgeElementType.TEXT && !(element.data as BadgeTextData).text?.trim()) {
      errors.push({ type: "empty_text", message: "No text", ...about })
    }

    // Only elements that may bleed can get there
    const box = getElementAABB(element, element.transforms?.rotate || 0)
    if (box.x >= badge.width || box.y >= badge.height
      || box.x + box.width <= 0 || box.y + box.height <= 0) {
      errors.push({ type: "off_badge", message: "Entirely outside the badge", ...about })
    }

    elementColors(element).forEach(({ fieldName, label, color }) => {
      if (!color || isColorAllowed(color, colorMode)) return

      errors.push({
        type: "incompatible_color",
        message: `${label} ${color}, but this label prints ${printableColors}`,
        ...about,
        fieldName,
        currentColor: color,
      })
    })
  })

  return errors
}
